import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Circle } from 'phosphor-react';
import QuestionAudioPlayer from '../shared/QuestionAudioPlayer';

// Get base URL for audio
const getBaseURL = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:3000';
};

const SingleCorrectAnswer = ({ question, onAnswer, onNext, sessionData = null, variant = 'default' }) => {
  const activeAnnotationRef = useRef(null);
  const annotationAudioRef = useRef(null);

  useEffect(() => {
    if (annotationAudioRef.current) {
      annotationAudioRef.current.pause();
      annotationAudioRef.current = null;
    }
  }, [question['soal-id'] || question.id]);

  // Transform backend data to component format
  const transformedQuestion = useMemo(() => {
    const options = [];

    if (question.choices && Array.isArray(question.choices)) {
      // JANGAN SORT - gunakan urutan asli dari backend
      // Urutan choices dari backend harus dipertahankan
      // PRIORITAS: Gunakan idx dari choices (bukan index array)
      question.choices.forEach((choice) => {
        // Pastikan menggunakan idx dari choices, BUKAN index array
        // choice.idx adalah idx dari backend (bisa 0, 2, 5, dll)
        // choice.id adalah fallback jika idx tidak ada
        const choiceIdx = choice.idx !== undefined ? choice.idx : choice.id;
        
        if (choiceIdx === undefined && process.env.NODE_ENV === 'development') {
          console.warn('⚠️ SingleCorrectAnswer - Choice missing idx and id:', choice);
        }
        
        options.push({
          id: choiceIdx, // Ini adalah idx dari choices, BUKAN index array
          text: choice['option-text'] || choice.text || '',
          isCorrect: choice.correct === true,
        });
      });
    }

    return {
      ...question,
      options: options,
      text: question.text || question['text-soal'] || '',
    };
  }, [question]);

  // Build annotations map from question.annotations array
  const annotationsMap = useMemo(() => {
    const map = {};
    
    // First, try to get annotations from question.annotations array
    if (question.annotations && Array.isArray(question.annotations)) {
      question.annotations.forEach((annotation) => {
        const annId = annotation['ann-id'] || annotation.annId || annotation.id;
        const streamUrl = annotation['stream-url'] || annotation.streamUrl || annotation.stream_url;
        const annType = annotation['ann-type'] || annotation.annType || annotation.type;
        
        // If streamUrl exists, treat it as audio annotation (for new format without ann-type)
        if (annId && streamUrl) {
          map[annId] = {
            annId,
            streamUrl,
            annType: annType || 'audio',
          };
        }
      });
    }
    
    // Also parse HTML for data-ann-id attributes (fallback)
    const html = transformedQuestion.text || '';
    if (html && typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      try {
        const parser = new window.DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const nodes = doc.querySelectorAll('[data-ann-id]');

        nodes.forEach((node) => {
          const annId = node.getAttribute('data-ann-id');
          const annPath = node.getAttribute('data-ann-path');
          const annType = node.getAttribute('data-ann-type');

          // Only add if not already in map (from annotations array)
          // If annPath exists, treat it as audio (for new format)
          if (annId && annPath && !map[annId]) {
            // Convert data-ann-path to streamUrl
            const baseURL = getBaseURL();
            const streamUrl = `${baseURL}/${annPath}`;
            
            map[annId] = {
              annId,
              streamUrl,
              annType: annType || 'audio',
            };
          }
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('SingleCorrectAnswer: failed to parse annotations from HTML', error);
        }
      }
    }

    return map;
  }, [question.annotations, transformedQuestion.text]);

  const questionLevelAudioUrl = question?.['audio-url'] || question?.audio_url || null;

  const firstAnnotationAudioUrl = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
    try {
      const doc = new window.DOMParser().parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');
      for (const node of nodes) {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]?.streamUrl) {
          return annotationsMap[annId].streamUrl;
        }
      }
    } catch {}
    return null;
  }, [transformedQuestion.text, annotationsMap]);

  const displayAudioUrl = questionLevelAudioUrl || firstAnnotationAudioUrl;

  // Enhance HTML with annotation classes; hide annotation shown via waveform player
  const enhancedHtml = useMemo(() => {
    const html = transformedQuestion.text || '';
    if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      return html;
    }

    try {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const nodes = doc.querySelectorAll('[data-ann-id]');

      nodes.forEach((node) => {
        const annId = node.getAttribute('data-ann-id');
        if (annId && annotationsMap[annId]) {
          const isShownAsWaveform = !questionLevelAudioUrl && annotationsMap[annId].streamUrl === firstAnnotationAudioUrl;
          if (isShownAsWaveform) {
            node.style.display = 'none';
            // Remove trailing <br> / empty text nodes so they don't render as phantom gap
            let next = node.nextSibling;
            while (next && (next.nodeName === 'BR' || (next.nodeType === 3 && next.textContent.trim() === ''))) {
              const toRemove = next;
              next = next.nextSibling;
              toRemove.parentNode.removeChild(toRemove);
            }
          } else {
            node.classList.add('practice-annotation');
            node.classList.add('annotation-hidden');
            if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
            if (!node.hasAttribute('role')) node.setAttribute('role', 'button');
            node.setAttribute('aria-label', 'Play audio');
          }
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('SingleCorrectAnswer: failed to enhance HTML', error);
      }
      return html;
    }
  }, [transformedQuestion.text, annotationsMap, questionLevelAudioUrl, firstAnnotationAudioUrl]);

  const highlightAnnotation = (element) => {
    if (!element) return;

    if (activeAnnotationRef.current && activeAnnotationRef.current !== element) {
      activeAnnotationRef.current.classList.remove('practice-annotation-active');
    }

    activeAnnotationRef.current = element;
    element.classList.add('practice-annotation-active');
  };

  const handlePlayAnnotation = (annotation) => {
    const streamUrl = annotation?.streamUrl;
    if (!streamUrl) return;

    if (!annotationAudioRef.current) {
      annotationAudioRef.current = new Audio();
      annotationAudioRef.current.addEventListener('ended', () => {
        if (activeAnnotationRef.current) {
          activeAnnotationRef.current.classList.remove('practice-annotation-active');
          activeAnnotationRef.current = null;
        }
      });
    }
    const audioEl = annotationAudioRef.current;
    audioEl.pause();
    audioEl.src = streamUrl;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
  };

  const findAnnotationTrigger = (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return target.closest('[data-ann-id]');
    }
    return null;
  };

  const handleAnnotationTrigger = (event) => {
    if (event.target.closest('[data-sc-option]')) return;

    if (!annotationsMap || Object.keys(annotationsMap).length === 0) {
      return;
    }

    const trigger = findAnnotationTrigger(event);
    if (!trigger) {
      return;
    }

    const annId = trigger.getAttribute('data-ann-id');
    if (!annId || !annotationsMap[annId]) {
      return;
    }

    const annotation = annotationsMap[annId];
    highlightAnnotation(trigger);
    handlePlayAnnotation(annotation);
  };

  const handleAnnotationKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleAnnotationTrigger(event);
  };

  const handleOptionSelect = (optionId) => {
    // optionId adalah idx dari choices (bukan index array)
    // Pastikan yang dikirim adalah idx dari choices
    const selectedOption = transformedQuestion.options.find(opt => opt.id === optionId);
    const correctOption = transformedQuestion.options.find(opt => opt.isCorrect);
    
    // Pastikan answer adalah idx dari choices
    const answerIdx = selectedOption ? selectedOption.id : optionId;
    const correct = correctOption && answerIdx === correctOption.id;
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('🔍 SingleCorrectAnswer - Submitting answer:', {
      //   optionId,
      //   answerIdx,
      //   selectedOption,
      //   correctOption,
      //   correct,
      //   options: transformedQuestion.options.map(opt => ({ id: opt.id, text: opt.text, isCorrect: opt.isCorrect })),
      // });
    }
    
    if (onAnswer) {
      onAnswer({
        questionId: question['soal-id'] || question.id,
        answer: answerIdx, // Pastikan ini adalah idx dari choices, bukan index array
        isCorrect: correct
      });
    }
    
    if (onNext) {
      onNext();
    }
  };

  const badgeGradient = variant === 'drill'
    ? 'from-[#345d8a] to-[#1e3d6f]'
    : 'from-[#12766d] to-[#236463]';

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.span
          className={`px-3.5 py-2 bg-gradient-to-br ${badgeGradient} text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1`}
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Single Correct Answer
        </motion.span>
      </div>
      
      <div className="flex flex-col gap-5">
        {displayAudioUrl && (
          <QuestionAudioPlayer audioUrl={displayAudioUrl} />
        )}
        <div 
          className="text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
          onClick={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationTrigger : undefined}
          onKeyDown={annotationsMap && Object.keys(annotationsMap).length > 0 ? handleAnnotationKeyDown : undefined}
          dangerouslySetInnerHTML={{ __html: enhancedHtml }}
        />
        
        <div className="flex flex-col gap-3">
          {transformedQuestion.options.map((option) => (
            <div
              key={option.id}
              data-sc-option
              className="option-item flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 bg-[var(--card-bg)] border-[rgba(18,118,109,0.2)] dark:border-[var(--border-color)] hover:border-[#12766d] hover:bg-[rgba(18,118,109,0.05)] hover:translate-x-1 dark:hover:border-[#14b8a6] dark:hover:bg-[rgba(20,184,166,0.08)] max-md:p-3.5 max-md:min-h-[44px]"
              onClick={() => handleOptionSelect(option.id)}
            >
              <div className="option-radio flex items-center justify-center min-w-[24px] h-6 text-[#12766d] dark:text-[#14b8a6] shrink-0">
                <Circle size={20} weight="regular" />
              </div>
              <span className="option-text flex-1 text-base text-[var(--text-primary)] leading-normal max-md:text-[15px]">{option.text}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default SingleCorrectAnswer;

